import { Add } from '@mui/icons-material';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import moment from 'moment';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { ModalService } from 'src/app/common/components/pru-modal/modal-service';
import { ModulePermissionProps } from 'src/app/common/module/types';
import { getRangeDate } from 'src/app/common/utils';
import { editSpecialDays } from 'src/app/modules/Event/network/specialDayCrud';
import { getSpecialLabel } from 'src/app/modules/Event/network/utils';
import { useSpecialDayListing } from 'src/app/modules/Event/pages/GreetingCard/SpecialDay/hooks/useSpecialDayListing';
import { specialDayPath } from 'src/app/modules/Event/pages/GreetingCard/SpecialDay/SpecialDayPage';
import { PublishStatus } from 'src/app/modules/Event/types/greeting-card-type';
import { SpecialDayItem } from '../../../../types/special-day-type';

type SpecialDayListingPageProps = ParamsProps & ModulePermissionProps;

const SpecialDayList: FC<SpecialDayListingPageProps> = ({
  initialParams,
  onChangeQueryParams,
  enableRead,
  enableCreate,
  enableUpdate,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const { filterState, specialDayList, isLoading, setFilterState, refreshData } = useSpecialDayListing({
    initialParams,
    onChangeQueryParams,
  });

  const checkFromDateError = () => {
    if (filterState.fromDate && filterState.toDate) {
      return moment(filterState.fromDate).isAfter(moment(filterState.toDate), 'day');
    }
    return false;
  };

  const checkToDateError = () => {
    if (filterState.fromDate && filterState.toDate) {
      return moment(filterState.toDate).isBefore(moment(filterState.fromDate), 'day');
    }
    return false;
  };

  const updateStatus = async (row: SpecialDayItem) => {
    const isPublished = row.status === PublishStatus.PUBLISHED;
    const payload = {
      ...row,
      status: isPublished ? PublishStatus.UNPUBLISHED : PublishStatus.PUBLISHED,
      togglePublish: true,
    };
    try {
      await editSpecialDays(payload);
      refreshData();
    } catch (error: any) {
      const code = error?.errors?.code || '';
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: 'Failed, please try again.',
          },
        ]),
      );
    }
  };

  const handleToggleStatus = async (row: SpecialDayItem) => {
    if (row.status === PublishStatus.PUBLISHED) {
      ModalService.confirm({
        title: 'Unpublish Special Day',
        description: 'Unpublishing a special day will also unpublish its associated Content and Templates.',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        onConfirm: () => updateStatus(row),
      });
      return;
    }

    await updateStatus(row);
  };

  return (
    <>
      <style>
        {`.MuiDialogContent-root {
          padding: 0 24px;
        }`}
      </style>
      <PruFilter
        title="Filter"
        updateFilterOnChange
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'name',
            initialValue: filterState.name,
            displayName: 'Special Day Name',
          },
          {
            type: PruFilterItemType.DATE,
            field: 'fromDate',
            initialDate: filterState.fromDate,
            placeholder: 'Please select',
            displayName: 'From Date',
            errorText: checkFromDateError() ? 'From date must be less than or equal to To date' : '',
          },
          {
            type: PruFilterItemType.DATE,
            field: 'toDate',
            initialDate: filterState.toDate,
            displayName: 'To Date',
            placeholder: 'Please select',
            errorText: checkToDateError() ? 'To date must be greater than or equal to From date' : '',
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'status',
            initialValue: filterState.status,
            displayName: 'Status',
            choices: [
              { displayName: Translation('component.status.all'), value: '' },
              { displayName: Translation('component.status.published'), value: PublishStatus.PUBLISHED },
              { displayName: Translation('component.status.unpublished'), value: PublishStatus.UNPUBLISHED },
            ],
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            pageNum: 1,
            name: data.name,
            status: data.status,
            ...getRangeDate(data.fromDate || null, data.toDate || null),
          })
        }
        fetchData={refreshData}
      />

      <PruTable
        disableBulkSelect
        dataSource={specialDayList?.values}
        isLoading={isLoading}
        onRefresh={refreshData}
        title="List"
        headerBtnDef={
          enableCreate
            ? [
                {
                  color: 'primary',
                  title: (
                    <>
                      <Add />
                      {Translation('app.button.add')}
                    </>
                  ),
                  onClick: () => enableCreate && history.push(`${specialDayPath}/create`),
                },
              ]
            : []
        }
        operationDef={[
          {
            title: 'Edit',
            tooltipText: 'Edit Specific Day',
            condition: (row) => enableUpdate && row.status === PublishStatus.UNPUBLISHED,
            onClick: (row) => history.push(`${specialDayPath}/${row.specialDayId}`),
          },
          {
            title: 'Publish',
            tooltipText: 'Publish Specific Day',
            condition: (row) => enableUpdate && row.status === PublishStatus.UNPUBLISHED,
            onClick: (row) => handleToggleStatus(row),
          },
          {
            title: 'Unpublish',
            tooltipText: 'Unpublish Specific Day',
            condition: (row) => enableUpdate && row.status === PublishStatus.PUBLISHED,
            onClick: (row) => handleToggleStatus(row),
          },
          {
            title: 'Copy',
            tooltipText: 'Copy Specific Day',
            condition: (row) => enableUpdate,
            onClick: (row) => history.push(`${specialDayPath}/create?ref=${row.specialDayId}`),
          },
        ]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: 'specialDayId',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'name',
            displayName: 'Name',
            renderData: (row) => {
              return row.name ?? getSpecialLabel(row, intl.locale, false);
            },
          },
          {
            keyIndex: 'startDate',
            displayName: 'Start Date',
            renderData: (row) => (row.startDate ? moment(row.startDate).format('DD-MM-YYYY hh:mm:ss') : ''),
          },
          {
            keyIndex: 'endDate',
            displayName: 'End Date',
            renderData: (row) => (row.endDate ? moment(row.endDate).format('DD-MM-YYYY hh:mm:ss') : ''),
          },
          {
            keyIndex: 'publishedAt',
            displayName: 'Publish Date',
            renderData: (row) => (row.publishedAt ? moment(row.publishedAt).format('DD-MM-YYYY hh:mm:ss') : ''),
          },
          {
            keyIndex: 'createdAt',
            align: 'center',
            displayName: 'Created Date',
            renderData: (row) => (row.createdAt ? moment(row.createdAt).format('DD-MM-YYYY hh:mm:ss') : ''),
          },
          {
            keyIndex: 'createdBy',
            align: 'center',
            displayName: 'Created By',
            renderData: (row) => row.createdBy,
          },
          {
            keyIndex: 'updatedAt',
            align: 'center',
            displayName: 'Last Updated Date',
            renderData: (row) => (row.updatedAt ? moment(row.updatedAt).format('DD-MM-YYYY hh:mm:ss') : ''),
          },
          {
            keyIndex: 'updatedBy',
            displayName: 'Last Updated By',
            renderData: (row) => row.updatedBy,
          },
        ]}
        totalPages={Math.ceil((specialDayList?.total ?? 0) / filterState.pageSize)}
        totalRecords={specialDayList?.total}
        defaultPageNumber={filterState.pageNum}
        defaultRowsPerPage={filterState.pageSize}
        onChangePage={(page, rowsPerPage) => {
          const next = { ...filterState, pageNum: page, pageSize: rowsPerPage };
          setFilterState(next);
          refreshData();
        }}
      />
    </>
  );
};

export default SpecialDayList;
